import React, { createContext, useContext, useState, useEffect } from 'react'
import { authService } from '../utils/authService'
import { 
  hasRole, 
  hasMinimumRole, 
  hasPermission, 
  getUserPermissions, 
  canAccessRoute,
  USER_ROLES 
} from '../utils/roleUtils'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(null)

  useEffect(() => {
    // Verificar si hay un token guardado al cargar la aplicación
    const savedToken = localStorage.getItem('domus_token')
    if (savedToken) {
      try {
        const userData = authService.verifyToken(savedToken)
        setUser(userData)
        setToken(savedToken)
      } catch (error) {
        console.error('Token inválido:', error)
        localStorage.removeItem('domus_token')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password)
      setUser(response.user)
      setToken(response.token)
      localStorage.setItem('domus_token', response.token)
      return response
    } catch (error) {
      throw new Error(error.message || 'Error al iniciar sesión')
    }
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('domus_token')
    authService.logout()
  }

  const register = async (email, password, name) => {
    try {
      const response = await authService.register(email, password, name)
      setUser(response.user)
      setToken(response.token)
      localStorage.setItem('domus_token', response.token)
      return response
    } catch (error) {
      throw new Error(error.message || 'Error al registrarse')
    }
  }

  // Funciones de utilidad para roles
  const userHasRole = (role) => hasRole(user, role)
  const userHasMinimumRole = (role) => hasMinimumRole(user, role)
  const userHasPermission = (permission) => hasPermission(user, permission)
  const userCanAccessRoute = (route) => canAccessRoute(user, route)
  
  // Verificar si el usuario está pendiente de aprobación
  const isPendingApproval = () => hasRole(user, USER_ROLES.PENDING_APPROVAL)
  
  // Verificar si el usuario es administrador
  const isAdmin = () => hasMinimumRole(user, USER_ROLES.ADMIN)
  
  // Verificar si el usuario es superadministrador
  const isSuperAdmin = () => hasRole(user, USER_ROLES.SUPERADMIN)

  const value = {
    user,
    token,
    login,
    logout,
    register,
    loading,
    // Funciones de roles
    hasRole: userHasRole,
    hasMinimumRole: userHasMinimumRole,
    hasPermission: userHasPermission,
    canAccessRoute: userCanAccessRoute,
    isPendingApproval,
    isAdmin,
    isSuperAdmin,
    permissions: getUserPermissions(user)
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}